'use client'

import Image from 'next/image'
import {
  ABHA_HERO_SUCCESS_SHOW_BADGE,
  ABHA_HERO_SUCCESS_TITLE_MOBILE,
  ABHA_HERO_SUCCESS_CREATE_ANOTHER_CARD_CTA,
  ABHA_HERO_WIDGET_SECONDARY_ARROW_ICON,
} from '@components/NewProduct/constants'
import { AbhaHeroCard } from '@components/NewProduct/AbhaHeroWidget/steps/AbhaHeroCard'
import { AbhaHeroSuccessContentSection } from '@components/NewProduct/AbhaHeroWidget/steps/AbhaHeroSuccessContentSection'
import { AbhaHeroSuccessActionButton } from '@components/NewProduct/AbhaHeroWidget/steps/AbhaHeroSuccessDownloadButton'
import { AbhaHeroSuccessLeftPanel } from '@components/NewProduct/AbhaHeroWidget/steps/AbhaHeroSuccessLeftPanel'
import { AbhaHeroSuccessPanelHeader } from '@components/NewProduct/AbhaHeroWidget/steps/AbhaHeroSuccessPanelHeader'
import { AbhaHeroSuccessRightPanel } from '@components/NewProduct/AbhaHeroWidget/steps/AbhaHeroSuccessRightPanel'
import type { AbhaSuccessPanelPlatformData } from '@components/NewProduct/AbhaHeroWidget/steps/abhaSuccessRightPanelTypes'
import {
  DEFAULT_ABHA_SUCCESS_CARD_VARIANT,
  resolveAbhaSuccessCardVariant,
} from '@components/NewProduct/AbhaHeroWidget/steps/abhaSuccessTypes'
import { normalizeSuccessPanelData } from '@components/NewProduct/AbhaHeroWidget/steps/normalizeSuccessPanelData'
import type { AbhaCardPreviewData } from '@helpers/abhaWidgetHelper'
import { getClassNames } from '@helpers/productWidgetHelper'
import '../abhaHeroSuccess.css'
import './abhaHeroSuccessRightPanel.css'
import PropertyControlledComponent from '@components/PropertyControlledComponent/PropertyControlledComponent'

export type AbhaHeroSuccessStepData = {
  cardVariant?: string
  mobileData?: AbhaSuccessPanelPlatformData
  desktopData?: AbhaSuccessPanelPlatformData
}

export type AbhaHeroSuccessStepProps = {
  data?: AbhaHeroSuccessStepData
  isMobile: boolean
  appDownloadLink: string
  cardData: AbhaCardPreviewData
  onDownloadApp: () => void
  onDownloadAbhaCard?: () => void
  onCreateAnotherCard?: () => void
  ctaAnalyticsProps?: Record<string, unknown>
}

export const AbhaHeroSuccessStep = ({
  data = {},
  isMobile,
  appDownloadLink,
  cardData,
  onDownloadApp,
  onDownloadAbhaCard,
  onCreateAnotherCard,
  ctaAnalyticsProps,
}: AbhaHeroSuccessStepProps) => {
  const cardVariant = resolveAbhaSuccessCardVariant(data?.cardVariant ?? DEFAULT_ABHA_SUCCESS_CARD_VARIANT)
  const isShowVariant = cardVariant === 'showAbhaNumber'

  const panelCardData: AbhaCardPreviewData = {
    ...cardData,
    relationshipLabel: undefined,
  }

  if (isMobile && isShowVariant) {
    const panelData = normalizeSuccessPanelData(data?.mobileData, {
      title: '',
      appLink: appDownloadLink,
    })
    const hasBottomSection =
      Boolean(panelData.header.title || panelData.header.description) || panelData.content.length > 0

    return (
      <div
        className={getClassNames(['abha-hero-success', 'abha-hero-success--mobile', 'abha-hero-success--mobile-show'], {
          'abha-hero-success--mobile-show-themed': panelData.panelVariant === 'themed',
        })}>
        <div className="abha-hero-success__mobile-show-top">
          <AbhaHeroSuccessPanelHeader
            header={{
              badgeText: panelData.header.badgeText ?? ABHA_HERO_SUCCESS_SHOW_BADGE,
              title: '',
            }}
            isMobile
            badgeVariant="onShowSurface"
            align="center"
            badgeOnly
          />

          <AbhaHeroCard
            data={panelCardData}
            layout="resolve"
            variant="showAbhaNumber"
            animateReveal={false}
            className="abha-hero-success__card abha-hero-success__card--mobile abha-hero-success__card--show"
            showDownloadAction
            onDownload={onDownloadAbhaCard}
          />

          <AbhaHeroSuccessActionButton
            label={ABHA_HERO_SUCCESS_CREATE_ANOTHER_CARD_CTA}
            onClick={() => onCreateAnotherCard?.()}
            fullWidth
            className="abha-hero-success__download-btn--mobile-show"
            trailingIcon={
              <Image
                src={ABHA_HERO_WIDGET_SECONDARY_ARROW_ICON}
                alt=""
                width={16}
                height={16}
                className="abha-hero-success__create-another-card-icon"
                aria-hidden
              />
            }
          />
        </div>

        <PropertyControlledComponent controllerProperty={hasBottomSection} fallback={null}>
          <div className="abha-hero-success__mobile-show-bottom">
            <PropertyControlledComponent
              controllerProperty={panelData?.header?.title || panelData?.header?.description}
              fallback={null}>
              <div className="abha-hero-success__mobile-show-bottom-header">
                {panelData?.header?.title ? (
                  <h2 className="abha-hero-success__mobile-show-bottom-title">{panelData?.header?.title}</h2>
                ) : null}
                {panelData?.header?.description ? (
                  <p className="abha-hero-success__mobile-show-bottom-description">{panelData?.header?.description}</p>
                ) : null}
              </div>
            </PropertyControlledComponent>

            <AbhaHeroSuccessContentSection
              panelData={panelData}
              appDownloadLink={appDownloadLink}
              isMobile
              onDownloadApp={onDownloadApp}
              onDownloadAbhaCard={onDownloadAbhaCard}
              ctaAnalyticsProps={ctaAnalyticsProps}
            />
          </div>
        </PropertyControlledComponent>
      </div>
    )
  }

  if (isMobile) {
    const panelData = normalizeSuccessPanelData(data?.mobileData, {
      title: ABHA_HERO_SUCCESS_TITLE_MOBILE,
      appLink: appDownloadLink,
    })

    const header = (
      <AbhaHeroSuccessPanelHeader
        header={{
          ...panelData.header,
          badgeText: panelData.header.badgeText,
        }}
        isMobile
        badgeVariant="onDark"
        align="center"
      />
    )

    const card = (
      <div className="abha-hero-success__hide-card-stage abha-hero-success__hide-card-stage--mobile">
        <AbhaHeroCard
          data={panelCardData}
          variant="hideAbhaNumber"
          className="abha-hero-success__card abha-hero-success__card--mobile abha-hero-success__card--mobile-hide"
        />
      </div>
    )

    const content = (
      <AbhaHeroSuccessContentSection
        panelData={panelData}
        appDownloadLink={appDownloadLink}
        isMobile
        onDownloadApp={onDownloadApp}
        onDownloadAbhaCard={onDownloadAbhaCard}
        ctaAnalyticsProps={ctaAnalyticsProps}
      />
    )

    return (
      <div
        className={getClassNames(['abha-hero-success', 'abha-hero-success--mobile', 'abha-hero-success--mobile-hide'], {
          'abha-hero-success--mobile-themed': panelData.panelVariant === 'themed',
        })}>
        <div className="abha-hero-success__mobile-body">
          {header}
          {card}
          {content}
        </div>
      </div>
    )
  }

  return (
    <div className="abha-hero-success abha-hero-success--desktop">
      <AbhaHeroSuccessLeftPanel
        cardVariant={cardVariant}
        cardData={cardData}
        badgeText={data?.desktopData?.showBadgeText}
        onDownloadAbhaCard={onDownloadAbhaCard}
        onCreateAnotherCard={onCreateAnotherCard}
      />
      <AbhaHeroSuccessRightPanel data={data} appDownloadLink={appDownloadLink} ctaAnalyticsProps={ctaAnalyticsProps} />
    </div>
  )
}
